import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { toast } from "../../../../../components/toastNotification";
import {
  addStateAgencyMapping,
  editStateAgencyMapping,
  agencyMappingsSaving,
  rowForEditAgencyMapping,
} from "../atoms/atoms";
import useAddAgencyMapping from "../hooks/useAddAgencyMapping";
import useEditAgencyMapping from "../hooks/useEditAgencyMapping";
import { IAgencyMappingsDetails } from "../types";
import AgencyMappingDetails from "./AgencyMappingDetails";
interface AgencyMappingFormProps {
  isAdd: boolean;
  isEdit: boolean;
  setIsAdd;
  setIsEdit;
}

export const AgencyMappingForm: React.FC<AgencyMappingFormProps> = ({
  isAdd,
  isEdit,
  setIsAdd,
  setIsEdit,
}) => {
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditAgencyMapping);
  const [saveModal, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(agencyMappingsSaving);
  const [isAddState, setAddState] = useRecoilState(addStateAgencyMapping);
  const setEditState = useSetRecoilState(editStateAgencyMapping);
  const queryClient = useQueryClient();

  const [newData, setNewData] = useState<IAgencyMappingsDetails>();
  const useFormMethods = useForm<IAgencyMappingsDetails>({
    defaultValues: { ...rowForEdit },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;

  // Add Agency Mapping
  const {
    mutate: agencyMappingForm,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddAgencyMapping();

  //Edit Business License & Permit Initialization/
  const {
    mutate: editAgencyMapping,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditAgencyMapping();

  const onSubmit = (data: IAgencyMappingsDetails) => {
    setNewData({
      ...data,
    });

    if (isAdd) {
      setSaveModal(true);
    } else if (isEdit && isDirty) {
      setSaveModal(true);
    } else {
      setIsSaving(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };
  const handleSave = () => {
    setSaveModal(false);
    if (isAddState) {
      agencyMappingForm(newData);
    } else {
      editAgencyMapping({ ...newData, isActive: !newData?.isActive });
    }
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setIsAdd(false);
    setIsEdit(false);
  };

  //Add Result
  if (addSuccess && addResult) {
    if (addResult.status === 501 || addResult.data === "Duplicate") {
      toast.fire("Failed", "Agency Mapping Record Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Agency Mapping Added Successfully.", "success");
      queryClient.invalidateQueries("GetAgencyMapping");
      //ssearchRefetch();
      handleCancel();
      setAddState(false);
      setIsAdd(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Agency Mapping added successfully, but " +
            addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Add Agency Mapping! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetAgencyMapping");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Agency Mapping! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetAgencyMapping");
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Agency Mapping! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetAgencyMapping");
    setAddState(false);
  }

  //Edit Result
  if (editSuccess && editResult) {
    if (editResult && editResult.data === "Duplicate") {
      toast.fire("Failed", "Agency Mapping Record Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (
      (editResult && editResult.status === 201) ||
      editResult.status === 200
    ) {
      toast.fire("Success", "Agency Mapping Updated Successfully.", "success");
      queryClient.invalidateQueries("GetAgencyMapping");
      //searchRefetch();
      handleCancel();
      setRowForEdit(undefined);
      setEditState(false);
      setIsEdit(false);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Agency Mapping updated successfully, but " +
            editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setIsSaving(false);
        setRowForEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to Update Agency Mapping! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetAgencyMapping");
      setEditState(false);
      setIsEdit(false);
    } else {
      setIsSaving(false);
      toast.fire(
        "Server Error",
        "Failed to Update Agency Mapping! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetAgencyMapping");
      setEditState(false);
      setIsEdit(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setSaveModal(false);
    setIsEdit(false);
    toast.fire(
      "Server Error",
      "Failed to Update Agency Mapping! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetAgencyMapping");
    setEditState(false);
    setIsEdit(false);
  }

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAdd || isAddState
                  ? "Add New Agency Mapping"
                  : "Edit Agency Mapping"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={isSaving}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <AgencyMappingDetails rowForEdit={rowForEdit} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState ? "Add Agency Mapping" : "Update Agency Mapping"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
